import type { ReadonlyHttpHeaders } from '../../../types/web/http';
import type { RequestPath, SslInfo } from '../../../types/web/server';
import type { PathContainer } from '../../../types/path';
import { AbstractHttpRequest } from '../exchange.ts';
import { parsePath, subPath } from './path.ts';

export function parseRequestPath(uri: string | URL, contextPath?: string) {
    if (typeof uri === 'string') {
        return DefaultRequestPath.parse(uri, contextPath);
    }
    else {
        return parseRequestPath(uri.href, contextPath);
    }
}

class DefaultRequestPath implements RequestPath {

    static #initContextPath(path: PathContainer, contextPath?: string): PathContainer {
        if (!(contextPath && contextPath.trim()) || contextPath.charAt(0) !== '/') {
            return parsePath('');
        }

        DefaultRequestPath.#validateContextPath(path.value, contextPath);

        const length = contextPath.length;
        let counter = 0;

        for (let i = 0; i < path.elements.length; i++) {
            const element = path.elements[i];
            counter += element.value.length;
            if (length === counter) {
                return subPath(path, 0, i + 1);
            }
        }

        // should not reach here
        throw new Error(`Failed to initialize context path: '${contextPath}' for request path: '${path.value}'`);
    }

    static #validateContextPath(fullPath: string, contextPath: string): void {
        const length = contextPath.length;
        if (contextPath.charAt(0) !== '/' || contextPath.charAt(length - 1) === '/') {
            throw new Error(`Invalid context path: '${contextPath}'. Context path must start with '/' and must not end with '/'`);
        }
        if (!fullPath.startsWith(contextPath)) {
            throw new Error(`Invalid context path: '${contextPath}'. Context path must be a prefix of request path: '${fullPath}'`);
        }
        if (fullPath.length > length && fullPath.charAt(length) !== '/') {
            throw new Error(`Invalid context path: '${contextPath}'. Context path must match to full path segments for request path: '${fullPath}'`);
        }
    }

    static #extractPathWithinApplication(fullPath: PathContainer, contextPath: PathContainer): PathContainer {
        const contextPathLength = contextPath.elements.length;
        return subPath(fullPath, contextPathLength);
    }

    static parse(rawPath: string, contextPath?: string) {
        const fullPath = parsePath(rawPath);
        const contextPathContainer = DefaultRequestPath.#initContextPath(fullPath, contextPath);
        const pathWithinApplication = DefaultRequestPath.#extractPathWithinApplication(fullPath, contextPathContainer);
        return new DefaultRequestPath(
            parsePath(rawPath),
            contextPathContainer,
            pathWithinApplication
        )
    }

    readonly #fullPath: PathContainer;
    readonly #contextPath: PathContainer;
    readonly #pathWithinApplication: PathContainer;

    private constructor(fullPath: PathContainer, contextPath: PathContainer, pathWithinApplication: PathContainer) {
        this.#fullPath = fullPath;
        this.#contextPath = contextPath;
        this.#pathWithinApplication = pathWithinApplication;
    }

    // path container
    get value() {
        return this.#fullPath.value;
    }

    get elements() {
        return this.#fullPath.elements;
    }

    // request path
    get contextPath() {
        return this.#contextPath;
    }

    get pathWithinApplication() {
        return this.#pathWithinApplication;
    }

    modifyContextPath(contextPath: string): RequestPath {
        const contextPathContainer = DefaultRequestPath.#initContextPath(this, contextPath);
        const pathWithinApplication = DefaultRequestPath.#extractPathWithinApplication(this, contextPathContainer);
        return new DefaultRequestPath(
            this,
            contextPathContainer,
            pathWithinApplication
        );
    }

    toString(): string {
        return this.#fullPath.toString();
    }
}

export abstract class AbstractServerHttpRequest extends AbstractHttpRequest<ReadonlyHttpHeaders> {
    #sslInfo?: SslInfo
    #path?: RequestPath
    #contextPath?: string;

    abstract getNativeRequest<T>(): T;

    get requestPath(): RequestPath {
        if (this.#path === undefined) {
            this.#path = parseRequestPath(this.URL.pathname, this.#contextPath)
        }
        return this.#path;
    }

    protected abstract initSslInfo(): SslInfo | undefined;

    get sslInfo(): SslInfo | undefined {
        if (this.#sslInfo === undefined) {
            this.#sslInfo = this.initSslInfo();
        }
        return this.#sslInfo;
    }
}
